/* eslint-disable react/jsx-one-expression-per-line */
import { Box, Grid } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { requestAccountDeletion } from 'src/services/account-deletion.service';
import { API_RESPONSE_CODE } from 'src/customer-call-sdk/config/constants';
import { useToast } from 'src/context/ToastContext';
import CustomerNumbers from './CustomerNumbers';
import {
    getUsedCustomerNumber,
    getOwnedCustomerNumber,
    getBusinessNumberOwnedByCustomer,
} from '../../services/customer.service';
import { CustomerNumber, BusinessNumber } from '../../types';
import PageTitle from '../common/PageTitle';
import { SITE_DESCRIPTION } from '../../config/seo.constants';
import SEO from '../common/SEO';
import UserLayout from '../UserLayout';
import withAuth from '../../hoc/withAuth';
import Container from '../common/Container';
import useUser from '../../hooks/useUser';
import useCustomerCallRedirect from '../../hooks/useCustomerCallRedirect';
import { useCall } from '../../customer-call-sdk';
import regionConfig from '../../config';
import DeleteAccountDialog from './DeleteAccountDialog';

const CustomerProfile = () => {
    useCustomerCallRedirect();
    const user = useUser();
    const { refreshedAccessToken } = useCall();

    const [ownednumbers, setOwnedNumbers] = useState<CustomerNumber[]>([]);
    const [usedNumbers, setUsedNumbers] = useState<CustomerNumber[]>([]);
    const [customerBusinessNumbers, setCustomerBusinessNumbers] = useState<BusinessNumber[]>([]);
    const [signLiveExt, setSignLiveExt] = useState<CustomerNumber[]>([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const toast = useToast();

    const handleAccountDeletion = async () => {
        setLoading(true);
        if (user?.uuid) {
            const result = await requestAccountDeletion(user?.uuid);
            if (result.code === API_RESPONSE_CODE.SUCCESS) {
                toast.success(result.message || '');
                setDeleteDialogOpen(false);
            } else {
                toast.error(result.message || '');
            }
            setLoading(false);
        }
    };

    const fetchCustomerNumbersOwned = async () => {
        if (user) {
            const { numbers } = await getOwnedCustomerNumber(user.uuid);
            if (numbers) {
                setOwnedNumbers(numbers);
            }
        }
    };

    const fetchUsedCustomerNumber = async () => {
        if (user) {
            const { numbers } = await getUsedCustomerNumber(user.uuid);
            if (numbers) {
                setUsedNumbers(numbers);
            }
        }
    };

    const fetchBusinessNumberOwnedByCustomer = async () => {
        if (user) {
            const { numbers } = await getBusinessNumberOwnedByCustomer(user.uuid);
            if (numbers) {
                setCustomerBusinessNumbers(numbers);
            }
        }
    };

    useEffect(() => {
        if (!refreshedAccessToken) {
            return;
        }
        if (user) {
            fetchCustomerNumbersOwned();
            fetchUsedCustomerNumber();
            fetchBusinessNumberOwnedByCustomer();
        }
    }, [user, refreshedAccessToken]);

    useEffect(() => {
        const extnNumbers = ownednumbers.filter((number: CustomerNumber) => {
            return number.numberTypeId === 'extn';
        });
        setSignLiveExt(extnNumbers);
    }, [ownednumbers]);

    return (
        <UserLayout>
            <SEO
                title="Customer Profile"
                description={`${regionConfig.appName} Customer Profile - ${SITE_DESCRIPTION}`}
            />
            <Container>
                <PageTitle id="customer-profile-info-title">User Profile</PageTitle>

                {user && (
                    <div id="customer-profile-info-div">
                        <div id="customer-profile-info-name-div" className="mb-3">
                            <span id="customer-profile-info-name-span" className="font-bold mr-2">
                                Name:
                            </span>
                            <span>{`${user.firstName} ${user.lastName}`}</span>
                        </div>
                        <div id="customer-profile-info-email-div" className="mb-3">
                            <span id="customer-profile-info-email-span" className="font-bold mr-2">
                                Email:
                            </span>
                            <span>{user.email}</span>
                        </div>
                        <div id="customer-profile-info-phone-ext-div" className="mb-3">
                            <span
                                id="customer-profile-info-phone-ext-span"
                                className="font-bold mr-2"
                            >
                                {regionConfig.appName} Phone Extension:
                            </span>
                            {signLiveExt?.map((n: any) => (
                                <span id={`customer-profile-info-${n.number}`} key={n.number}>
                                    {n.number}
                                </span>
                            ))}
                        </div>
                        <Grid
                            id="customer-profile-edit-profile-container"
                            container
                            style={{ marginTop: '50px' }}
                        >
                            <Grid id="customer-profile-edit-btn-container" size={{ xs: 4 }}>
                                <Box display="flex">
                                    {/* <Link
                                        id="customer-profile-edit-link"
                                        to={{
                                            pathname: `/profile/edit/${user.uuid}`,
                                            state: { cust: user },
                                        }}
                                    >
                                        <button
                                            id="customer-profile-edit-btn"
                                            type="button"
                                            style={{
                                                color: 'white',
                                                border: '1px solid green',
                                                borderRadius: '5px',
                                                backgroundColor: 'green',
                                                padding: '10px',
                                            }}
                                            aria-label="Edit Profile"
                                        >
                                            Edit Profile
                                        </button>
                                    </Link> */}
                                    <button
                                        id="customer-profile-new-btn"
                                        type="button"
                                        style={{
                                            borderRadius: '5px',
                                            border: '1px solid #DF352C',
                                            padding: '10px',
                                            color: '#DF352C',
                                            fontWeight: 400,
                                            marginLeft: '10px',
                                        }}
                                        aria-label="New Action"
                                        onClick={() => setDeleteDialogOpen(true)}
                                    >
                                        Delete Account
                                    </button>
                                </Box>
                            </Grid>
                        </Grid>
                    </div>
                )}
                <CustomerNumbers
                    ownednumbers={ownednumbers}
                    usedNumbers={usedNumbers}
                    customerBusinessNumbers={customerBusinessNumbers}
                />
            </Container>
            <DeleteAccountDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onDeleted={handleAccountDeletion}
                loading={loading}
            />
        </UserLayout>
    );
};

export default withAuth(CustomerProfile);
